import React, { useMemo, useEffect, useState, useRef, useCallback, useSyncExternalStore } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import dissolve from '@turf/dissolve';
import { featureCollection } from '@turf/helpers';
import ARRL_SECTIONS, { getCountySection, getStateSection, getCanadaSection, getCountrySection, SPLIT_STATES } from '../data/arrlSections';
import ONTARIO_SECTION_GEOJSON from '../data/ontarioSections';

// Reactive system color scheme detection
const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
function subscribeDarkMode(cb) {
  darkModeQuery.addEventListener('change', cb);
  return () => darkModeQuery.removeEventListener('change', cb);
}
function useIsDarkMode() {
  return useSyncExternalStore(subscribeDarkMode, () => darkModeQuery.matches);
}

const SECTION_COLORS = [
  '#5eadb8', '#6ba88f', '#8a7ab3', '#b8855e', 
  '#a87a8f', '#b8a85e', '#6e8fb8', '#6ba87a',
  '#9a7ab3', '#b87a7a', '#5ea8a0', '#b8a05e',
  '#7a7fb3', '#b87a8a', '#8aa86e', '#5ea8b8'
];

function assignColorsToSections(workedSections) {
  const assignments = {};
  const workedList = Object.keys(workedSections).sort();
  workedList.forEach((section, index) => {
    assignments[section] = index % SECTION_COLORS.length;
  });
  return assignments;
}

function SectionLabels({ sectionCounts, sectionCenters, colorAssignments, sectionBounds, isDark }) {
  const map = useMap();
  const markersRef = useRef([]);
  const paneCreated = useRef(false);
  
  const updateLabels = useCallback(() => {
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    
    const mapBounds = map.getBounds();
    const mapWidth = mapBounds.getEast() - mapBounds.getWest();
    const mapHeight = mapBounds.getNorth() - mapBounds.getSouth();
    const mapArea = mapWidth * mapHeight;

    const labelBg = isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)';
    const labelShadow = isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.15)';

    Object.entries(sectionCounts).forEach(([section, count]) => {
      if (count <= 0) return;
      if (section === 'DX') return;
      
      // Skip sections without centers
      if (!sectionCenters[section]) return;
      
      const bounds = sectionBounds?.[section];
      if (bounds) {
        const sectionWidth = Math.abs(bounds.maxLng - bounds.minLng);
        const sectionHeight = Math.abs(bounds.maxLat - bounds.minLat);
        const sectionArea = sectionWidth * sectionHeight;
        
        if (sectionArea / mapArea < 0.005) return;
      }
      
      const center = sectionCenters[section];
      const color = SECTION_COLORS[colorAssignments[section] % SECTION_COLORS.length];
      
      const icon = L.divIcon({
        className: 'section-label',
        html: `<div style="
          background: ${labelBg};
          color: ${color};
          padding: 6px 10px;
          border-radius: 6px;
          font-weight: bold;
          font-size: 12px;
          text-align: center;
          white-space: nowrap;
          border: 2px solid ${color};
          box-shadow: ${labelShadow};
          pointer-events: none;
        ">
          <div style="font-size: 11px; opacity: 0.9;">${section}</div>
          <div style="font-size: 18px;">${count}</div>
        </div>`,
        iconSize: [60, 50],
        iconAnchor: [30, 25]
      });
      
      const marker = L.marker(center, { icon, interactive: false, pane: 'labelsPane' });
      marker.addTo(map);
      markersRef.current.push(marker);
    });
  }, [map, sectionCounts, sectionCenters, colorAssignments, sectionBounds, isDark]);
  
  useEffect(() => {
    if (!paneCreated.current) {
      map.createPane('labelsPane');
      map.getPane('labelsPane').style.zIndex = 610;
      paneCreated.current = true;
    }
    
    updateLabels();
    
    map.on('zoomend', updateLabels);
    map.on('moveend', updateLabels);
    
    return () => {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      map.off('zoomend', updateLabels);
      map.off('moveend', updateLabels);
    };
  }, [map, updateLabels]);
  
  return null;
}

function DXLabel({ dxCount }) {
  if (!dxCount || dxCount <= 0) return null;
  
  const color = '#f59e0b';
  
  return (
    <div style={{
      position: 'absolute',
      bottom: '20px',
      left: '20px',
      background: 'rgba(15, 23, 42, 0.95)',
      color: color,
      padding: '8px 12px',
      borderRadius: '8px',
      fontWeight: 'bold',
      fontSize: '14px',
      textAlign: 'center',
      border: `2px solid ${color}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
      zIndex: 1000
    }}>
      <div style={{ fontSize: '12px', opacity: 0.9 }}>DX</div>
      <div style={{ fontSize: '20px' }}>{dxCount}</div>
    </div>
  );
}

function MapAutoZoom({ markers, clubQth }) {
  const map = useMap();
  const prevMarkersCount = useRef(0);
  
  useEffect(() => {
    if (!clubQth?.lat) return;
    if (markers.length === 0 && prevMarkersCount.current === 0) return;
    
    const markersChanged = markers.length !== prevMarkersCount.current;
    prevMarkersCount.current = markers.length;
    
    if (!markersChanged && markers.length > 0) return;
    
    if (markers.length === 0) {
      map.flyTo([clubQth.lat, clubQth.lon], 5, { duration: 1 });
      return;
    }
    
    const clubWeight = 0.5;
    const contactWeight = 0.5 / markers.length;
    
    let weightedLat = clubQth.lat * clubWeight;
    let weightedLon = clubQth.lon * clubWeight;
    
    markers.forEach(m => {
      weightedLat += m.lat * contactWeight;
      weightedLon += m.lon * contactWeight;
    });
    
    const allPoints = [[clubQth.lat, clubQth.lon], ...markers.map(m => [m.lat, m.lon])];
    const bounds = L.latLngBounds(allPoints);
    
    map.flyTo([weightedLat, weightedLon], map.getBoundsZoom(bounds.pad(0.1)), {
      duration: 1.5
    });
  }, [map, markers, clubQth]);
  
  return null;
}

function ContactMarkers({ markers, clubQth }) {
  const map = useMap();
  const markersRef = useRef([]);
  const linesRef = useRef([]);
  const clubMarkerRef = useRef(null);
  const paneCreated = useRef(false);
  
  useEffect(() => {
    if (!paneCreated.current) {
      map.createPane('linesPane');
      map.getPane('linesPane').style.zIndex = 620;
      map.createPane('pinsPane');
      map.getPane('pinsPane').style.zIndex = 630;
      paneCreated.current = true;
    }
    
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    linesRef.current.forEach(l => l.remove());
    linesRef.current = [];
    if (clubMarkerRef.current) {
      clubMarkerRef.current.remove();
      clubMarkerRef.current = null;
    }
    
    if (clubQth && clubQth.lat && clubQth.lon) {
      const clubIcon = L.divIcon({
        className: 'club-marker',
        html: `<div style="
          width: 16px;
          height: 16px;
          background: #22c55e;
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.5);
        "></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      
      const clubMarker = L.marker([clubQth.lat, clubQth.lon], { icon: clubIcon, pane: 'pinsPane' });
      clubMarker.bindTooltip(
        `<strong>${clubQth.callsign || 'Club QTH'}</strong>${clubQth.grid ? `<br/>Grid: ${clubQth.grid}` : ''}`,
        { direction: 'top', offset: [0, -10] }
      );
      clubMarker.addTo(map);
      clubMarkerRef.current = clubMarker;
      
      markers.forEach(m => {
        const line = L.polyline(
          [[clubQth.lat, clubQth.lon], [m.lat, m.lon]],
          { 
            color: '#ffffff', 
            weight: 3, 
            opacity: 0.4,
            pane: 'linesPane'
          }
        );
        line.addTo(map);
        linesRef.current.push(line);
      });
    }
    
    markers.forEach(m => {
      const icon = L.divIcon({
        className: 'contact-marker',
        html: `<div style="
          width: 12px;
          height: 12px;
          background: #f472b6;
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        "></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
      
      const marker = L.marker([m.lat, m.lon], { icon, pane: 'pinsPane' });
      marker.bindTooltip(
        `<strong>${m.callsign}</strong>${m.grid ? `<br/>Grid: ${m.grid}` : ''}${m.city && m.state ? `<br/>${m.city}, ${m.state}` : ''}`,
        { direction: 'top', offset: [0, -8] }
      );
      marker.addTo(map);
      markersRef.current.push(marker);
    });
    
    return () => {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      linesRef.current.forEach(l => l.remove());
      linesRef.current = [];
      if (clubMarkerRef.current) {
        clubMarkerRef.current.remove();
        clubMarkerRef.current = null;
      }
    };
  }, [map, markers, clubQth]);
  
  return null;
}

// GeoJSON province name differences vs ARRL data
const PROVINCE_NAME_MAP = { 'Yukon Territory': 'Yukon' };

function ContactMap({ sectionCounts, contactMarkers = [], clubQth = null }) {
  const isDark = useIsDarkMode();
  const [countyData, setCountyData] = useState(null);
  const [stateData, setStateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionCenters, setSectionCenters] = useState({});
  const [sectionBounds, setSectionBounds] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const [countyRes, stateRes, canadaRes, mexicoRes] = await Promise.all([
          fetch('https://raw.githubusercontent.com/plotly/datasets/master/geojson-counties-fips.json'),
          fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json'),
          fetch('https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/canada.geojson'),
          fetch('https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/mexico.geojson')
        ]);
        
        const counties = await countyRes.json();
        const states = await stateRes.json();
        const canada = await canadaRes.json();
        const mexico = await mexicoRes.json();
        
        const mexicoFeature = {
          type: 'Feature',
          properties: { name: 'Mexico' },
          geometry: {
            type: 'MultiPolygon',
            coordinates: mexico.features.map(f => 
              f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates
            ).flat()
          }
        };
        
        const combinedStates = {
          type: 'FeatureCollection',
          features: [...states.features, ...canada.features, mexicoFeature]
        };
        
        setCountyData(counties);
        setStateData(combinedStates);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load GeoJSON:', err);
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const colorAssignments = useMemo(() => {
    return assignColorsToSections(sectionCounts);
  }, [sectionCounts]);

  // Compute centers/bounds for Ontario sub-sections from their polygon geometry
  useEffect(() => {
    const centers = {};
    const bounds = {};
    ONTARIO_SECTION_GEOJSON.features.forEach(feature => {
      const section = feature.properties.section;
      try {
        const geoBounds = L.geoJSON(feature).getBounds();
        centers[section] = [geoBounds.getCenter().lat, geoBounds.getCenter().lng];
        bounds[section] = {
          minLat: geoBounds.getSouth(),
          maxLat: geoBounds.getNorth(),
          minLng: geoBounds.getWest(),
          maxLng: geoBounds.getEast()
        };
      } catch (e) {}
    });
    setSectionCenters(prev => ({ ...prev, ...centers }));
    setSectionBounds(prev => ({ ...prev, ...bounds }));
  }, []);

  const processedGeoData = useMemo(() => {
    if (!stateData) return null;
    
    const centers = {};
    const bounds = {};
    
    const features = stateData.features.map(feature => {
        const rawName = feature.properties.name;
        const name = PROVINCE_NAME_MAP[rawName] || rawName;
        const isSplitState = SPLIT_STATES.includes(name);
        
        // Ontario is rendered as sub-sections via ONTARIO_SECTION_GEOJSON — treat as transparent
        const isOntario = name === 'Ontario';

        let section = null;
        if (!isSplitState && !isOntario) {
          section = getStateSection(name) || getCanadaSection(name) || getCountrySection(name);
        }
        
        if (section) {
          const geoBounds = L.geoJSON(feature).getBounds();
          centers[section] = [geoBounds.getCenter().lat, geoBounds.getCenter().lng];
          bounds[section] = {
            minLat: geoBounds.getSouth(),
            maxLat: geoBounds.getNorth(),
            minLng: geoBounds.getWest(),
            maxLng: geoBounds.getEast()
          };
        }
        
        return {
          ...feature,
          properties: {
            ...feature.properties,
            name,
            section,
            isSplitState: isSplitState || isOntario  // Ontario handled separately
          }
        };
      });
    
    setSectionCenters(prev => ({ ...prev, ...centers }));
    setSectionBounds(prev => ({ ...prev, ...bounds }));
    
    return { type: 'FeatureCollection', features };
  }, [stateData]);

  const processedCountyData = useMemo(() => {
    if (!countyData) return null;
    
    const FIPS_TO_STATE = {
      '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas',
      '06': 'California', '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware',
      '11': 'District of Columbia', '12': 'Florida', '13': 'Georgia', '15': 'Hawaii',
      '16': 'Idaho', '17': 'Illinois', '18': 'Indiana', '19': 'Iowa',
      '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana', '23': 'Maine',
      '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota',
      '28': 'Mississippi', '29': 'Missouri', '30': 'Montana', '31': 'Nebraska',
      '32': 'Nevada', '33': 'New Hampshire', '34': 'New Jersey', '35': 'New Mexico',
      '36': 'New York', '37': 'North Carolina', '38': 'North Dakota', '39': 'Ohio',
      '40': 'Oklahoma', '41': 'Oregon', '42': 'Pennsylvania', '44': 'Rhode Island',
      '45': 'South Carolina', '46': 'South Dakota', '47': 'Tennessee', '48': 'Texas',
      '49': 'Utah', '50': 'Vermont', '51': 'Virginia', '53': 'Washington',
      '54': 'West Virginia', '55': 'Wisconsin', '56': 'Wyoming', '72': 'Puerto Rico'
    };
    
    const sectionCounties = {};
    const sectionBoundsTemp = {};
    
    const unmatchedCounties = [];
    countyData.features.forEach(feature => {
      const fips = feature.id || feature.properties.GEOID;
      if (!fips) return;
      const stateFips = fips.substring(0, 2);
      const stateName = FIPS_TO_STATE[stateFips];
      if (!SPLIT_STATES.includes(stateName)) return;
      
      const countyName = feature.properties.NAME || feature.properties.name || '';
      const section = getCountySection(stateName, countyName);
      
      if (section) {
        if (!sectionCounties[section]) {
          sectionCounties[section] = [];
          sectionBoundsTemp[section] = { lats: [], lngs: [] };
        }
        sectionCounties[section].push(feature);
        try {
          const bounds = L.geoJSON(feature).getBounds();
          sectionBoundsTemp[section].lats.push(bounds.getCenter().lat);
          sectionBoundsTemp[section].lngs.push(bounds.getCenter().lng);
        } catch (e) {}
      } else {
        unmatchedCounties.push({ state: stateName, county: countyName });
      }
    });
    
    console.log('Section processing:', {
      totalSplitStateCounties: Object.values(sectionCounties).flat().length,
      sections: Object.keys(sectionCounties),
      unmatchedCount: unmatchedCounties.length,
      floridaSections: Object.keys(sectionCounties).filter(s => ['NFL', 'SFL', 'WCF'].includes(s)),
      countiesPerSection: Object.fromEntries(Object.entries(sectionCounties).map(([k, v]) => [k, v.length]))
    });
    if (unmatchedCounties.length > 0) {
      console.warn('Unmatched counties:', unmatchedCounties);
    }
    
    const features = Object.entries(sectionCounties).map(([section, counties]) => {
      try {
        const polygonFeatures = counties.map(county => {
          if (county.geometry.type === 'Polygon') {
            return {
              type: 'Feature',
              properties: { section },
              geometry: county.geometry
            };
          } else if (county.geometry.type === 'MultiPolygon') {
            return {
              type: 'Feature', 
              properties: { section },
              geometry: county.geometry
            };
          }
          return null;
        }).filter(Boolean);
        
        if (polygonFeatures.length === 0) return null;
        
        const fc = featureCollection(polygonFeatures);
        
        let resultGeometry;
        let dissolved = false;
        try {
          const dissolvedResult = dissolve(fc, { propertyName: 'section' });
          if (dissolvedResult.features.length > 0) {
            resultGeometry = dissolvedResult.features[0].geometry;
            dissolved = true;
          }
        } catch (dissolveError) {
          // Dissolve failed, will use fallback
        }
        
        if (!resultGeometry) {
          const allCoords = [];
          polygonFeatures.forEach(f => {
            if (f.geometry.type === 'Polygon') {
              allCoords.push(f.geometry.coordinates);
            } else if (f.geometry.type === 'MultiPolygon') {
              f.geometry.coordinates.forEach(c => {
                allCoords.push(c);
              });
            }
          });
          resultGeometry = { type: 'MultiPolygon', coordinates: allCoords };
        }
        
        return {
          type: 'Feature',
          properties: {
            section,
            name: ARRL_SECTIONS[section]?.name || section,
            dissolved
          },
          geometry: resultGeometry
        };
      } catch (e) {
        console.error(`Failed to merge section ${section}:`, e);
        return null;
      }
    }).filter(Boolean);
    
    const centers = {};
    const boundsData = {};
    Object.entries(sectionBoundsTemp).forEach(([section, b]) => {
      if (b.lats.length > 0) {
        const avgLat = b.lats.reduce((a, c) => a + c, 0) / b.lats.length;
        const avgLng = b.lngs.reduce((a, c) => a + c, 0) / b.lngs.length;
        centers[section] = [avgLat, avgLng];
        boundsData[section] = {
          minLat: Math.min(...b.lats) - 1,
          maxLat: Math.max(...b.lats) + 1,
          minLng: Math.min(...b.lngs) - 1,
          maxLng: Math.max(...b.lngs) + 1
        };
      }
    });
    
    setSectionCenters(prev => ({ ...prev, ...centers }));
    setSectionBounds(prev => ({ ...prev, ...boundsData }));
    
    return { type: 'FeatureCollection', features };
  }, [countyData]);

  const styleState = (feature) => {
    const { section, isSplitState } = feature.properties;
    
    if (isSplitState) {
      return {
        fillColor: 'transparent',
        weight: 2,
        opacity: 1,
        color: 'rgba(148, 163, 184, 0.5)',
        fillOpacity: 0
      };
    }
    
    const isWorked = section && sectionCounts[section] > 0;
    const color = isWorked ? SECTION_COLORS[colorAssignments[section] % SECTION_COLORS.length] : null;
    
    return {
      fillColor: isWorked ? color : 'transparent',
      weight: isWorked ? 2 : 1,
      opacity: 1,
      color: isWorked ? color : 'rgba(148, 163, 184, 0.4)',
      fillOpacity: isWorked ? 0.5 : 0
    };
  };

  const styleOntario = (feature) => {
    const { section } = feature.properties;
    const isWorked = section && sectionCounts[section] > 0;
    const color = isWorked ? SECTION_COLORS[colorAssignments[section] % SECTION_COLORS.length] : null;
    return {
      fillColor: isWorked ? color : 'transparent',
      weight: isWorked ? 2 : 1,
      opacity: 1,
      color: isWorked ? color : 'rgba(148, 163, 184, 0.4)',
      fillOpacity: isWorked ? 0.5 : 0
    };
  };

  const styleSection = (feature) => {
    const { section, dissolved } = feature.properties;
    const isWorked = section && sectionCounts[section] > 0;
    const color = isWorked ? SECTION_COLORS[colorAssignments[section] % SECTION_COLORS.length] : null;
    
    return {
      fillColor: isWorked ? color : 'transparent',
      weight: dissolved ? 2 : 0,
      opacity: 1,
      color: dissolved ? (isWorked ? color : 'rgba(148, 163, 184, 0.5)') : 'transparent',
      fillOpacity: isWorked ? 0.5 : 0
    };
  };

  const onEachState = (feature, layer) => {
    const { section, name, isSplitState } = feature.properties;
    // Ontario is rendered via sub-section layer — suppress province-level tooltip
    if (name === 'Ontario' || isSplitState) return;
    const sectionInfo = section ? ARRL_SECTIONS[section] : null;
    const sectionName = sectionInfo?.name || name;
    const count = section ? (sectionCounts[section] || 0) : 0;
    layer.bindTooltip(
      `<strong>${section || name || '?'}</strong> - ${sectionName}<br/>Contacts: ${count}`,
      { sticky: true }
    );
  };

  const onEachOntario = (feature, layer) => {
    const { section, name } = feature.properties;
    const count = sectionCounts[section] || 0;
    layer.bindTooltip(
      `<strong>${section}</strong> - ${name}<br/>Contacts: ${count}`,
      { sticky: true }
    );
  };

  const onEachSection = (feature, layer) => {
    const { section, name } = feature.properties;
    const count = section ? (sectionCounts[section] || 0) : 0;
    
    layer.bindTooltip(
      `<strong>${section || '?'}</strong> - ${name}<br/>Contacts: ${count}`,
      { sticky: true }
    );
  };

  const center = [39.8283, -98.5795];

  return (
    <MapContainer
      center={center}
      zoom={4}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={true}
    >
      <TileLayer
        key={isDark ? 'dark' : 'light'}
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
        url={isDark
          ? "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
          : "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
        }
      />
      {processedGeoData && (
        <GeoJSON 
          key={`states-${Object.keys(sectionCounts).sort().join(',')}`}
          data={processedGeoData} 
          style={styleState}
          onEachFeature={onEachState}
        />
      )}
      {processedCountyData && (
        <GeoJSON 
          key={`sections-${Object.keys(sectionCounts).sort().join(',')}`}
          data={processedCountyData} 
          style={styleSection}
          onEachFeature={onEachSection}
          pane="overlayPane"
        />
      )}
      <GeoJSON
        key={`ontario-${Object.keys(sectionCounts).sort().join(',')}`}
        data={ONTARIO_SECTION_GEOJSON}
        style={styleOntario}
        onEachFeature={onEachOntario}
        pane="overlayPane"
      />
      {!loading && (
        <>
          <SectionLabels 
            sectionCounts={sectionCounts} 
            sectionCenters={sectionCenters}
            colorAssignments={colorAssignments}
            sectionBounds={sectionBounds}
            isDark={isDark}
          />
          <ContactMarkers markers={contactMarkers} clubQth={clubQth} />
          <MapAutoZoom markers={contactMarkers} clubQth={clubQth} />
        </>
      )}
      <DXLabel dxCount={sectionCounts['DX'] || 0} />
      {loading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.8)',
          padding: '1.5rem',
          borderRadius: '0.75rem',
          color: 'white',
          zIndex: 1000,
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '0.5rem' }}>Loading ARRL sections...</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>Fetching boundary data</div>
        </div>
      )}
    </MapContainer>
  );
}

export default ContactMap;
